import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { InGameCurrency, Rarity, Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { FontAwesomeIcon } from 'atoms/icons';
import { Boost } from 'atoms/icons/Boost';
import { Energy } from 'atoms/icons/Energy';
import { GemIcon } from 'atoms/icons/GemIcon';
import { Shards } from 'atoms/icons/Shards';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { BrandL, BrandS, LabelS } from 'atoms/typography';
import { DialogKey } from 'components/navigation/WithDialogs';
import {
  ANY_SPORT_ESSENCE,
  ANY_SPORT_ESSENCE_TYPE,
  MLB_MARKET_COMMON_MARKET,
} from 'constants/__generated__/routes';
import { ANY_SPORT_MARKET_COMMON_PACKS } from 'constants/routes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useSportContext } from 'contexts/sport';
import { useSeriesNav } from 'hooks/commonGame/useSeriesNav';
import { usePinnedShardRarity } from 'hooks/config/usePinnedShardRarity';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useGetMyCardsBoostLink } from 'hooks/my-cards/useGetMyCardsBoostLink';
import { useOpenDialog } from 'hooks/navigation/useOpenDialog';
import { LIFECYCLE, useLifecycle } from 'hooks/useLifecycle';
import { formatScarcity } from 'lib/cards';
import { getRarityColor } from 'lib/colors';
import { glossary } from 'lib/glossary';
import { inGameCurrencyToRarity } from 'lib/scarcity';
import { laptopAndAbove } from 'style/mediaQuery';
import type { Color } from 'style/types';

import { PinButton } from './PinButton';
import { ShardDropdownDetails } from './ShardDropdownDetails';

export enum InGameCurrencyAgreggated {
  ENERGY = 'ENERGY',
  XP = 'XP',
  SHARDS = 'SHARDS',
  BLUEPRINTS = 'BLUEPRINT',
  GEMS = 'GEMS',
}

type DetailsProps = { onClick?: () => void };

export type CurrencyData = {
  id: InGameCurrencyAgreggated;
  key: string;
  icon: ReactNode;
  amount?: number;
  details?: (props: DetailsProps) => ReactNode;
  visibleOnSports: Sport[];
  expanded?: boolean;
  onClick?: () => void;
};

const SelectedCurrencyWrapper = styled(Vertical).attrs({
  gap: 2,
})`
  position: relative;
  padding: var(--double-unit) var(--unit) var(--unit) var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--intermediate-unit);
`;
const RaritiesWrapper = styled(Horizontal)`
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: column;
    gap: var(--half-unit);
  }
`;
const RarityWrapper = styled(Vertical)`
  justify-content: space-between;
  align-items: center;
  flex: 1;
  text-align: center;
  border-radius: var(--unit);
  padding: var(--unit);
  background-color: var(--c-nd-50);

  @media ${laptopAndAbove} {
    flex-direction: row;
    width: 100%;
    padding: var(--half-unit) var(--unit);

    background-color: transparent;
    &:hover {
      background-color: var(--c-nd-50);
    }
  }
`;
const RarityContainer = styled(Vertical).attrs({ center: true })`
  width: 100%;
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

const messages = defineMessages({
  capAlmostReached: {
    id: 'useUserCurrencies.capAlmostReached',
    defaultMessage: 'Cap almost reached',
  },
});

const MIX_SHARD_COLOR: Color = 'var(--c-green-400)';
const MIX_XP_COLOR: Color = 'var(--c-rewards-blue)';

export const useUserCurrencies = (
  expandedCurrencies?: InGameCurrencyAgreggated[]
): CurrencyData[] => {
  const navigate = useNavigate();

  const { sport, generateSportContextPath } = useSportContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const { currentUser } = useCurrentUserContext();
  const [seeAllEnergy, setSeeAllEnergy] = useState(false);
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const { pinnedShardRarity, pinnedShardCardSet } = usePinnedShardRarity();
  const isLaptop = useIsLaptop();
  const seriesNav = useSeriesNav();
  const { energyBalance, xpBalances, cardShardsChests } = currentUser || {};

  const filteredXpBalances = xpBalances?.filter(balance =>
    lifecycle?.hideCommonFeatures
      ? balance.currency !== InGameCurrency.COMMON_XP
      : true
  );
  const totalEnergyBalance = energyBalance?.reduce(
    (sum, { currency, amount }) => {
      const rarity = inGameCurrencyToRarity[currency];
      if (!rarity) return sum;
      // do not show energy balance if user has no card of this rarity
      if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
        return sum;
      }
      return sum + amount;
    },
    0
  );

  const { pinnedXpRarity } = lifecycle || {};

  const shardsAmount = pinnedShardRarity
    ? cardShardsChests?.find(
        ({ rarity, cardSet }) =>
          rarity === pinnedShardRarity && cardSet?.slug === pinnedShardCardSet
      )?.cardShardsCount || 0
    : cardShardsChests
        ?.filter(
          ({ rarity }) =>
            !lifecycle?.hideCommonFeatures || rarity !== Rarity.common
        )
        .reduce((sum, { cardShardsCount }) => sum + cardShardsCount, 0);
  const firstNonZeroShardsBalance = (cardShardsChests || []).find(
    ({ cardShardsCount }) => cardShardsCount > 0
  );
  const firstNonZeroShardsBalanceRarity = firstNonZeroShardsBalance?.rarity;

  const xpAmount = pinnedXpRarity
    ? filteredXpBalances?.find(
        ({ currency }) => inGameCurrencyToRarity[currency] === pinnedXpRarity
      )?.amount || 0
    : filteredXpBalances?.reduce((sum, { amount }) => sum + amount, 0);

  const firstNonZeroXPBalance = (filteredXpBalances || []).find(
    ({ amount }) => amount > 0
  );
  const firstNonZeroXPBalanceRarity = firstNonZeroXPBalance?.currency
    ? inGameCurrencyToRarity[firstNonZeroXPBalance.currency]
    : undefined;

  const gemsCount = currentUser?.gemsBalances[0]?.amount || 0;

  const pinCurrencyRarity = (rarity: Rarity) => {
    if (!lifecycle) {
      return;
    }
    updateLifecycle(
      LIFECYCLE.pinnedXpRarity,
      lifecycle?.[LIFECYCLE.pinnedXpRarity] === rarity ? false : rarity
    );
  };

  const openDialog = useOpenDialog();

  return [
    {
      id: InGameCurrencyAgreggated.SHARDS,
      key: `${InGameCurrencyAgreggated.SHARDS}_${pinnedShardRarity ?? 'all'}_${pinnedShardCardSet ?? 'all'}`,
      icon: (
        <Shards
          size={2}
          color={
            pinnedShardRarity
              ? getRarityColor(pinnedShardRarity, {
                  cardSet: pinnedShardCardSet,
                })
              : MIX_SHARD_COLOR
          }
        />
      ),
      amount: shardsAmount,
      details: ({ onClick }: DetailsProps) => (
        <ShardDropdownDetails onClick={onClick} />
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.SHARDS),
      onClick: () => {
        const rarity = pinnedShardRarity || firstNonZeroShardsBalanceRarity;
        if (rarity === Rarity.common) {
          navigate(
            sport === Sport.BASEBALL
              ? MLB_MARKET_COMMON_MARKET
              : generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)
          );
        } else if (rarity) {
          navigate(
            generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
              params: { type: rarity },
            })
          );
        } else {
          navigate(generateSportContextPath(ANY_SPORT_ESSENCE));
        }
      },
    },
    {
      id: InGameCurrencyAgreggated.XP,
      key: InGameCurrencyAgreggated.XP,
      icon: (
        <Boost
          size={2}
          color={pinnedXpRarity ? getRarityColor(pinnedXpRarity) : MIX_XP_COLOR}
        />
      ),
      amount: xpAmount,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Boost
              size={2}
              color={
                pinnedXpRarity ? getRarityColor(pinnedXpRarity) : MIX_XP_COLOR
              }
            />
            <BrandS
              color={
                pinnedXpRarity ? getRarityColor(pinnedXpRarity) : MIX_XP_COLOR
              }
            >
              <FormattedMessage
                id="XpBalances.xpReserve"
                defaultMessage="XP Reserve"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={xpAmount || 0} />
            </BrandL>
          </Vertical>
          <RaritiesWrapper>
            {filteredXpBalances?.map(
              ({ currency, amount, capAlmostReached }) => {
                const rarity = inGameCurrencyToRarity[currency];
                if (!rarity) return null;
                return (
                  <RarityContainer key={currency}>
                    {!!isLaptop && (
                      <PinButton
                        onClick={() => pinCurrencyRarity(rarity)}
                        isPinned={pinnedXpRarity === rarity}
                      />
                    )}
                    <RarityWrapper
                      as={Link}
                      to={getMyCardsBoostLink({
                        rarity,
                        sport,
                      })}
                      onClick={onClick}
                    >
                      <LabelS>{formatScarcity(rarity)}</LabelS>
                      <Horizontal>
                        <BrandS>
                          <FormattedNumber value={amount} />
                        </BrandS>
                        {!!capAlmostReached && (
                          <Tooltip
                            title={
                              <FormattedMessage
                                {...messages.capAlmostReached}
                              />
                            }
                          >
                            <FontAwesomeIcon
                              size="xs"
                              color="var(--c-yellow-400)"
                              icon={faTriangleExclamation}
                            />
                          </Tooltip>
                        )}
                      </Horizontal>
                    </RarityWrapper>
                    {!isLaptop && (
                      <PinButton
                        onClick={() => pinCurrencyRarity(rarity)}
                        isPinned={pinnedXpRarity === rarity}
                      />
                    )}
                  </RarityContainer>
                );
              }
            )}
          </RaritiesWrapper>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.XP),
      onClick: () => {
        const rarity = lifecycle?.pinnedXpRarity || firstNonZeroXPBalanceRarity;
        if (rarity) {
          navigate(getMyCardsBoostLink({ rarity, sport }));
        } else {
          navigate(
            sport === Sport.BASEBALL
              ? MLB_MARKET_COMMON_MARKET
              : generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)
          );
        }
      },
    },
    {
      id: InGameCurrencyAgreggated.ENERGY,
      key: InGameCurrencyAgreggated.ENERGY,
      icon: <Energy size={2} color="var(--c-yellow-300)" />,
      amount: totalEnergyBalance,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Energy size={2} color="var(--c-yellow-300)" />
            <BrandS color="var(--c-yellow-300)">
              <FormattedMessage
                id="useUserCurrencies.energy"
                defaultMessage="Energy"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={totalEnergyBalance || 0} />
            </BrandL>
          </Vertical>
          <RaritiesWrapper gap={0}>
            {energyBalance?.map(({ currency, amount, capAlmostReached }) => {
              const rarity = inGameCurrencyToRarity[currency];
              if (!rarity) return null;
              // do not show energy balance if user has no card of this rarity
              if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
                return null;
              }
              return (
                <RarityWrapper key={currency}>
                  <LabelS>{formatScarcity(rarity)}</LabelS>
                  <Horizontal>
                    <BrandS>
                      <FormattedNumber value={amount} />
                    </BrandS>
                    {!!capAlmostReached && (
                      <Tooltip
                        title={
                          <FormattedMessage {...messages.capAlmostReached} />
                        }
                      >
                        <FontAwesomeIcon
                          size="xs"
                          color="var(--c-yellow-400)"
                          icon={faTriangleExclamation}
                        />
                      </Tooltip>
                    )}
                  </Horizontal>
                </RarityWrapper>
              );
            })}
          </RaritiesWrapper>
          <Button
            size="compact"
            fullWidth
            color="quaternary"
            disableDebounce
            onClick={() => setSeeAllEnergy(prev => !prev)}
          >
            <FormattedMessage
              {...(seeAllEnergy ? glossary.showLess : glossary.showMore)}
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.ENERGY),
    },
    {
      id: InGameCurrencyAgreggated.GEMS,
      key: InGameCurrencyAgreggated.GEMS,
      icon: <GemIcon size={2} />,
      amount: gemsCount,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <GemIcon size={2} />
            <BrandS color="var(--c-green-400)">
              <FormattedMessage
                id="useUserCurrencies.gems"
                defaultMessage="Gems"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={gemsCount || 0} />
            </BrandL>
          </Vertical>
          <Button
            size="medium"
            fullWidth
            color="quaternary"
            onClick={() => {
              openDialog(DialogKey.buyGems, 'true');
              onClick?.();
            }}
          >
            <FormattedMessage
              id="useUserCurrencies.getGems"
              defaultMessage="Get Gems"
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      onClick: () => openDialog(DialogKey.buyGems, 'true'),
      visibleOnSports: [Sport.FOOTBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.GEMS),
    },
  ]
    .filter(
      currency => currency.id !== InGameCurrencyAgreggated.XP || !seriesNav
    )
    .filter(Boolean);
};
